// src/components/WorkerProfileSelector.tsx
import React, { useEffect, useState } from 'react';
import { useFormikContext, FieldArray } from 'formik';
import {
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
	ISaleOperationRoleBackEnd,
	ISkill,
	ILevel,
	apiGetAllSkills,
	apiGeLevelBySkillId,
} from '../../services/apiService';
import { IProjectFormBackend } from '../../services/apiService';

interface SkillLevelPair {
	skillId: number | '';
	levelId: number | '';
}

interface WorkerProfileSelectorProps {
	index: number;
	saleOperationRoleOptions: ISaleOperationRoleBackEnd[];
	setWorkerProfiles: React.Dispatch<
		React.SetStateAction<IProjectFormBackend['saleOperationRoles']>
	>;
}

export default function WorkerProfileSelector({
	index,
	saleOperationRoleOptions,
	setWorkerProfiles,
}: WorkerProfileSelectorProps) {
	const { values, setFieldValue } = useFormikContext<IProjectFormBackend>();

	// useEffect(() => {
	// 	setWorkerProfiles(values.saleOperationRoles);
	// }, [values.saleOperationRoles, setWorkerProfiles]);

	const path = `saleOperationRoles[${index}]`;
	const roleId = values.saleOperationRoles[index]?.saleOperationRoleId || '';
	const skillLevels: SkillLevelPair[] =
		values.saleOperationRoles[index]?.skillLevel || [];

	const [allSkills, setAllSkills] = useState<ISkill[]>([]);
	const [levelsByPair, setLevelsByPair] = useState<{ [key: string]: ILevel[] }>(
		{}
	);

	useEffect(() => {
		apiGetAllSkills().then(setAllSkills);
	}, []);

	useEffect(() => {
		// para cada pair pré‑carregado, busca a lista de níveis
		skillLevels.forEach((pair, pairIndex) => {
			if (!pair.skillId) return;
			const key = `${index}-${pairIndex}`;
			// se ainda não carregou esses níveis
			if (!levelsByPair[key]) {
				apiGeLevelBySkillId(pair.skillId).then(fetched => {
					setLevelsByPair(prev => ({ ...prev, [key]: fetched }));
				});
			}
		});
	}, [index, skillLevels, setLevelsByPair, levelsByPair]);

	const addPair = () => {
		setFieldValue(`${path}.skillLevel`, [
			...skillLevels,
			{ skillId: '', levelId: '' },
		]);
	};

	return (
		<Grid
			container
			spacing={3}
			alignItems="center"
			sx={{ px: 1, py: 1 }} // espaço horizontal e vertical
		>
			<Grid item xs={4}>
				<FormControl fullWidth>
					<InputLabel>Perfil do Funcionário {index + 1}</InputLabel>
					<Select
						label={`Perfil do Funcionário ${index + 1}`}
						name={`${path}.saleOperationRoleId`}
						value={String(roleId)}
						onChange={e =>
							setFieldValue(
								`${path}.saleOperationRoleId`,
								Number(e.target.value)
							)
						}
					>
						{saleOperationRoleOptions.map(opt => (
							<MenuItem key={opt.id} value={opt.id}>
								{opt.description}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Grid>

			<Grid item>
				<IconButton size="small" onClick={addPair} sx={{ ml: 1 }}>
					<AddIcon />
				</IconButton>
			</Grid>

			<FieldArray name={`${path}.skillLevel`}>
				{arrayHelpers =>
					skillLevels.map((pair, pairIndex) => {
						const key = `${index}-${pairIndex}`;
						const levels = levelsByPair[key] || [];
						return (
							<Grid
								container
								spacing={2}
								alignItems="center"
								key={pairIndex}
								sx={{ mt: 1 }}
							>
								<Grid item xs={12} sm={4} sx={{ ml: 3 }}>
									<FormControl fullWidth>
										<InputLabel>Habilidade</InputLabel>
										<Select
											label="Habilidade"
											value={pair.skillId}
											onChange={async e => {
												const skillId = Number(e.target.value);
												setFieldValue(
													`${path}.skillLevel[${pairIndex}].skillId`,
													skillId
												);
												setFieldValue(
													`${path}.skillLevel[${pairIndex}].levelId`,
													''
												);
												const fetched = await apiGeLevelBySkillId(skillId);
												setLevelsByPair(prev => ({ ...prev, [key]: fetched }));
											}}
										>
											{allSkills.map(s => (
												<MenuItem key={s.id} value={s.id}>
													{s.description}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={3}>
									<FormControl fullWidth>
										<InputLabel>Nível</InputLabel>
										<Select
											label="Nível"
											value={pair.levelId}
											onChange={e =>
												setFieldValue(
													`${path}.skillLevel[${pairIndex}].levelId`,
													Number(e.target.value)
												)
											}
										>
											{levels.map(l => (
												<MenuItem key={l.id} value={l.id}>
													{l.description}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								<Grid item>
									<IconButton
										size="small"
										onClick={() => arrayHelpers.remove(pairIndex)}
										sx={{ ml: 1 }}
									>
										<DeleteIcon />
									</IconButton>
								</Grid>
							</Grid>
						);
					})
				}
			</FieldArray>
		</Grid>
	);
}
